import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useSharedInterval from '@/hooks/useSharedInterval';

export const getJackpotIncrement = (
  intervalId: string,
  tickerInterval: number,
  defaultUpdateInterval: number,
  initialValueCalculation: ({
    estimatedJackpotValue,
    jackpotValue,
    previousJackpotValue,
    timestamp,
    incrementValue,
  }: {
    estimatedJackpotValue: number;
    jackpotValue: number;
    previousJackpotValue: number;
    timestamp: number;
    incrementValue: number;
  }) => number
) => (
  jackpotValue: number,
  jackpotTimestamp: number,
  incrementValue: number = 0,
  jackpotUpdateInterval: number = defaultUpdateInterval
) => {
  const prevJackpotValue = useRef(0);
  const estimatedJackpotValue = useRef(0);

  const tickerBaseValue = useMemo(() => {
    return initialValueCalculation({
      estimatedJackpotValue: estimatedJackpotValue.current,
      jackpotValue,
      incrementValue,
      timestamp: jackpotTimestamp,
      previousJackpotValue: prevJackpotValue.current,
    });
  }, [jackpotValue, incrementValue, jackpotTimestamp]);

  const velocity = useMemo(() => {
    return (
      incrementValue ||
      calculateVelocity(tickerBaseValue, jackpotValue, jackpotUpdateInterval)
    );
  }, [incrementValue, tickerBaseValue, jackpotValue, jackpotUpdateInterval]);

  const getJackpotDistance = useCallback(
    (timestamp: number) => ((timestamp - jackpotTimestamp) / 1000) * velocity,
    [jackpotTimestamp, velocity]
  );

  const [jackpot, setJackpot] = useState(
    tickerBaseValue + getJackpotDistance(Date.now())
  );

  useEffect(() => {
    prevJackpotValue.current = jackpotValue;
    setJackpot(tickerBaseValue + getJackpotDistance(Date.now()));
  }, [jackpotValue, tickerBaseValue, getJackpotDistance]);

  const updateJackpotValue = useCallback(
    (timestamp: number) => {
      const newJp = tickerBaseValue + getJackpotDistance(Date.now());
      if (newJp < jackpotValue || incrementValue) setJackpot(newJp);
    },
    [incrementValue, jackpotValue, tickerBaseValue, getJackpotDistance]
  );

  useSharedInterval(intervalId, updateJackpotValue, tickerInterval);
  estimatedJackpotValue.current = jackpot;
  return jackpot;
};

const calculateVelocity = (
  initialValue: number,
  actualValue: number,
  jackpotUpdateInterval: number
) => {
  const tickDistance = actualValue - initialValue;
  const nrOfTicks = jackpotUpdateInterval / 1000;
  return Math.max(0, tickDistance / nrOfTicks);
};
